import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './Sudoku.css';

const Sudoku = () => {
  const [board, setBoard] = useState(() => generateSudoku());
  const [initialBoard, setInitialBoard] = useState(() => generateSudoku());
  const [selectedCell, setSelectedCell] = useState(null);
  const [errors, setErrors] = useState(new Set());
  const [completed, setCompleted] = useState(false);

  function generateSudoku() {
    // Create a complete valid sudoku board
    const board = Array(9).fill().map(() => Array(9).fill(0));
    
    // Fill the board with a valid solution
    solveSudoku(board);
    
    // Remove numbers to create puzzle (keep about 30-40 numbers)
    const puzzle = board.map(row => [...row]);
    const cellsToRemove = 45; // Remove 45 out of 81 cells
    
    for (let i = 0; i < cellsToRemove; i++) {
      let row, col;
      do {
        row = Math.floor(Math.random() * 9);
        col = Math.floor(Math.random() * 9);
      } while (puzzle[row][col] === 0);
      
      puzzle[row][col] = 0;
    }
    
    return puzzle;
  }

  function solveSudoku(board) {
    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        if (board[row][col] === 0) {
          for (let num = 1; num <= 9; num++) {
            if (isValidMove(board, row, col, num)) {
              board[row][col] = num;
              if (solveSudoku(board)) {
                return true;
              }
              board[row][col] = 0;
            }
          }
          return false;
        }
      }
    }
    return true;
  }

  function isValidMove(board, row, col, num) {
    // Check row
    for (let x = 0; x < 9; x++) {
      if (board[row][x] === num) return false;
    }

    // Check column
    for (let x = 0; x < 9; x++) {
      if (board[x][col] === num) return false;
    }

    // Check 3x3 box
    const startRow = row - (row % 3);
    const startCol = col - (col % 3);
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        if (board[i + startRow][j + startCol] === num) return false;
      }
    }

    return true;
  }

  const handleCellClick = (row, col) => {
    if (initialBoard[row][col] === 0) {
      setSelectedCell([row, col]);
    }
  };

  const handleNumberInput = useCallback((num) => {
    if (selectedCell && !completed) {
      const [row, col] = selectedCell;
      if (initialBoard[row][col] === 0) {
        const newBoard = board.map(r => [...r]);
        newBoard[row][col] = num;
        setBoard(newBoard);
        
        // Check for errors
        const newErrors = new Set();
        if (num !== 0 && !isValidMove(board, row, col, num)) {
          newErrors.add(`${row}-${col}`);
        }
        setErrors(newErrors);
        
        // Check if completed
        if (isBoardComplete(newBoard) && newErrors.size === 0) {
          setCompleted(true);
        }
      }
    }
  }, [selectedCell, completed, initialBoard, board]);

  const isBoardComplete = (board) => {
    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        if (board[row][col] === 0) return false;
      }
    }
    return true;
  };

  const newGame = () => {
    const newBoard = generateSudoku();
    setBoard(newBoard);
    setInitialBoard(newBoard.map(row => [...row]));
    setSelectedCell(null);
    setErrors(new Set());
    setCompleted(false);
  };

  const clearCell = useCallback(() => {
    if (selectedCell) {
      const [row, col] = selectedCell;
      if (initialBoard[row][col] === 0) {
        const newBoard = board.map(r => [...r]);
        newBoard[row][col] = 0;
        setBoard(newBoard);
        setErrors(new Set());
      }
    }
  }, [selectedCell, initialBoard, board]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      const num = parseInt(e.key);
      if (num >= 1 && num <= 9) {
        handleNumberInput(num);
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        clearCell();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleNumberInput, clearCell]);

  return (
    <div className="game-container">
      <div className="game-header">
        <h1 className="game-title-header">🔢 Sudoku</h1>
        <div className="game-instructions">
          Fill the 9×9 grid so that each row, column, and 3×3 box contains digits 1-9.
        </div>
      </div>

      <div className="game-board">
        <div className="score-display">
          {completed ? '🎉 Congratulations! You solved it!' : 'Fill the grid with numbers 1-9'}
        </div>

        <div className="game-play-area">
          <div className="sudoku-board">
            {board.map((row, rowIndex) =>
              row.map((cell, colIndex) => (
                <div
                  key={`${rowIndex}-${colIndex}`}
                  className={`sudoku-cell ${
                    initialBoard[rowIndex][colIndex] !== 0 ? 'initial' : ''
                  } ${
                    selectedCell && selectedCell[0] === rowIndex && selectedCell[1] === colIndex
                      ? 'selected'
                      : ''
                  } ${
                    errors.has(`${rowIndex}-${colIndex}`) ? 'error' : ''
                  }`}
                  onClick={() => handleCellClick(rowIndex, colIndex)}
                >
                  {cell || ''}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="sudoku-number-controls">
          <div className="number-pad">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
              <button
                key={num}
                onClick={() => handleNumberInput(num)}
                className="number-button"
              >
                {num}
              </button>
            ))}
          </div>
          <button onClick={clearCell} className="clear-button">Clear</button>
        </div>
      </div>

      <div className="game-controls">
        <button className="btn btn-primary" onClick={newGame}>
          New Game
        </button>
        <Link to="/" className="btn btn-secondary">
          Back to Home
        </Link>
      </div>
    </div>
  );
};

export default Sudoku;